import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
// GitHub-flavoured markdown. react-markdown ships CommonMark only, which has
// no table syntax — so every pipe table in squish-docs rendered as literal
// "| col | col |" text. 71 of the 87 synced docs contain one.
import remarkGfm from 'remark-gfm';
import { fetchDocs, createDoc, updateDoc, deleteDoc, uploadDocFile, getDocFileUrl, getHeaders } from '../api';

function Loading() {
  return <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>;
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const CATEGORIES = [
  { id: null, label: 'All' },
  { id: 'session-updates', label: 'Session Updates' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'products', label: 'Products' },
  { id: 'runbooks', label: 'Runbooks' },
  { id: 'handoffs', label: 'Handoffs' },
  { id: 'files', label: 'Files' },
];

function categoryIcon(cat) {
  const map = { 'session-updates': '🤖', strategy: '📊', architecture: '🏗️', products: '📦', runbooks: '📋', handoffs: '🤝', files: '📎' };
  return map[cat] || '📄';
}

function categoryBadge(cat) {
  const map = { 'session-updates': 'badge-blue', strategy: 'badge-purple', architecture: 'badge-blue', products: 'badge-green', runbooks: 'badge-yellow', handoffs: 'badge-purple', files: 'badge-gray' };
  return map[cat] || 'badge-gray';
}

// ─── Doc Detail Side Panel ──────────────────────────────────────────────────
function DocDetail({ doc, onClose, onUpdated, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(doc.Content__c || '');
  const [editTitle, setEditTitle] = useState(doc.Title__c || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc.Id, { title: editTitle, content: editContent });
      onUpdated();
      setEditing(false);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePin = async () => {
    try {
      await updateDoc(doc.Id, { pinned: !doc.Pinned__c });
      onUpdated();
    } catch (err) {
      alert('Pin failed: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc.Id);
      onDeleted();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const isFile = !!doc.R2_Key__c;
  const isImage = doc.File_Type__c && doc.File_Type__c.startsWith('image/');

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: isFile ? '85vw' : 640, maxWidth: isFile ? 1200 : 640, background: 'var(--bg)', borderLeft: '1px solid var(--border)', zIndex: 101, display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.4)', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 12, position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>{categoryIcon(doc.Category__c)}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{doc.Category__c}</span>
              {doc.Pinned__c && <span style={{ fontSize: 10, color: 'var(--accent3)' }}>📌 Pinned</span>}
            </div>
            {editing ? (
              <input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 700, outline: 'none' }} />
            ) : (
              <div style={{ fontSize: 15, fontWeight: 700 }}>{doc.Title__c}</div>
            )}
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
              {doc.Author__c} · {formatDateTime(doc.CreatedDate)}
              {doc.Tags__c && <span style={{ marginLeft: 8, color: 'var(--text3)' }}>· {doc.Tags__c}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: '0 4px', flexShrink: 0 }}>✕</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '20px 24px' }}>
          {/* File preview */}
          {isFile && (
            <div style={{ marginBottom: 20, background: '#f5f5f5', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {isImage ? (
                <img src={getDocFileUrl(doc.Id)} alt={doc.File_Name__c} style={{ width: '100%', display: 'block' }} />
              ) : (
                <div style={{ padding: 24, textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)' }}>{doc.File_Name__c}</div>
                  <a href={getDocFileUrl(doc.Id)} target="_blank" rel="noreferrer" style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', marginTop: 8, display: 'inline-block' }}>
                    Download ↓
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Markdown content */}
          {editing ? (
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              rows={20}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.7, resize: 'vertical', outline: 'none' }}
            />
          ) : (
            <div style={{ fontFamily: 'var(--font)', fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }} className="doc-markdown">
              <ReactMarkdown skipHtml remarkPlugins={[remarkGfm]}>{doc.Content__c || '*No content*'}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {editing ? (
            <>
              <button onClick={handleSave} disabled={saving} style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setEditing(false); setEditContent(doc.Content__c || ''); setEditTitle(doc.Title__c || ''); }} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Cancel</button>
            </>
          ) : (
            <>
              <button data-write onClick={() => setEditing(true)} style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>Edit</button>
              <button data-write onClick={handlePin} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
                {doc.Pinned__c ? 'Unpin' : 'Pin'}
              </button>
              {confirmDelete ? (
                <>
                  <span style={{ fontSize: 11, color: 'var(--accent2)', alignSelf: 'center' }}>Delete?</span>
                  <button onClick={handleDelete} style={{ padding: '7px 12px', background: 'var(--accent2)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>Yes, delete</button>
                  <button onClick={() => setConfirmDelete(false)} style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Cancel</button>
                </>
              ) : (
                <button data-write onClick={() => setConfirmDelete(true)} style={{ padding: '7px 16px', background: 'transparent', border: '1px solid rgba(250,109,138,0.3)', color: 'var(--accent2)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', marginLeft: 'auto' }}>Delete</button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── New Doc Modal ──────────────────────────────────────────────────────────
function NewDocModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('strategy');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleCreate = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await createDoc({ title: title.trim(), content: content.trim(), category, tags: tags.trim() });
      onCreated();
    } catch (err) {
      alert('Create failed: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(560px, 94vw)', maxHeight: '85vh', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, zIndex: 101, display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>New Document</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document title" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', marginBottom: 14 }} />

          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                <option value="strategy">Strategy</option>
                <option value="architecture">Architecture</option>
                <option value="products">Products</option>
                <option value="runbooks">Runbooks</option>
                <option value="handoffs">Handoffs</option>
                <option value="files">Files</option>
                <option value="session-updates">Session Updates</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Tags</label>
              <input value={tags} onChange={e => setTags(e.target.value)} placeholder="product, hosting" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }} />
            </div>
          </div>

          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Content (Markdown)</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} rows={12} placeholder="Write your doc in Markdown..." style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.7, resize: 'vertical', outline: 'none' }} />
        </div>
        <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Cancel</button>
          <button data-write onClick={handleCreate} disabled={saving || !title.trim()} style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', opacity: saving || !title.trim() ? 0.5 : 1 }}>
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Upload Modal ───────────────────────────────────────────────────────────
function UploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('strategy');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      if (!title) setTitle(f.name);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title.trim() || file.name);
      formData.append('description', description.trim());
      formData.append('tags', tags.trim());
      formData.append('category', category);
      await uploadDocFile(formData);
      onUploaded();
    } catch (err) {
      alert('Upload failed: ' + err.message);
      setUploading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(480px, 94vw)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, zIndex: 101, boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Upload File</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>File</label>
          <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.md,.txt,.doc,.docx" onChange={handleFileChange} style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, marginBottom: 14 }} />
          {file && <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', marginTop: -8, marginBottom: 14 }}>{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}

          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 2 }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                <option value="strategy">Strategy</option>
                <option value="architecture">Architecture</option>
                <option value="products">Products</option>
                <option value="runbooks">Runbooks</option>
                <option value="handoffs">Handoffs</option>
                <option value="files">Files</option>
                <option value="session-updates">Session Updates</option>
              </select>
            </div>
          </div>

          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Optional description..." style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.6, resize: 'vertical', outline: 'none', marginBottom: 14 }} />

          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Tags</label>
          <input value={tags} onChange={e => setTags(e.target.value)} placeholder="strategy, product" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }} />
        </div>
        <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Cancel</button>
          <button data-write onClick={handleUpload} disabled={uploading || !file} style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', opacity: uploading || !file ? 0.5 : 1 }}>
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Docs Page ─────────────────────────────────────────────────────────
export default function Docs() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (category) params.category = category;
    fetchDocs(params)
      .then(data => { setDocs(data.docs || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const filtered = docs.filter(d => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (d.Title__c || '').toLowerCase().includes(s) ||
           (d.Content__c || '').toLowerCase().includes(s) ||
           (d.Tags__c || '').toLowerCase().includes(s) ||
           (d.Author__c || '').toLowerCase().includes(s);
  });

  const pinned = filtered.filter(d => d.Pinned__c);
  const unpinned = filtered.filter(d => !d.Pinned__c);

  const handleDocUpdated = () => { load(); setSelectedDoc(null); };
  const handleDocDeleted = () => { load(); setSelectedDoc(null); };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <h1 className="page-title">Docs</h1>
            <p className="page-subtitle">Strategy, runbooks, files, and session updates</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowUpload(true)} style={{ padding: '8px 14px', background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--mono)' }}>Upload File</button>
            <button onClick={() => setShowNewDoc(true)} style={{ padding: '8px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>+ New Doc</button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id || 'all'}
            onClick={() => setCategory(cat.id)}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 11, fontFamily: 'var(--mono)',
              cursor: 'pointer', transition: 'all 0.15s', fontWeight: 500,
              background: category === cat.id ? 'rgba(124,109,250,0.12)' : 'transparent',
              border: category === cat.id ? '1px solid rgba(124,109,250,0.3)' : '1px solid transparent',
              color: category === cat.id ? 'var(--accent)' : 'var(--text3)',
            }}
          >
            {cat.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search docs..."
          style={{ width: 200, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 11, outline: 'none' }}
        />
      </div>

      {error && <div className="error-state">Error: {error}</div>}
      {loading && <Loading />}

      {!loading && !error && (
        <>
          {/* Pinned */}
          {pinned.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>📌 Pinned</div>
              <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                {pinned.map(doc => (
                  <div key={doc.Id} className="stat-card" onClick={() => setSelectedDoc(doc)} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 14 }}>{categoryIcon(doc.Category__c)}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{doc.Category__c}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{doc.Title__c}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)' }}>{doc.Author__c} · {formatDate(doc.CreatedDate)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All docs */}
          {unpinned.length > 0 ? (
            <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
              {unpinned.map(doc => {
                const isFile = !!doc.R2_Key__c;
                const preview = (doc.Content__c || '').slice(0, 150);
                return (
                  <div key={doc.Id} className="stat-card" onClick={() => setSelectedDoc(doc)} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 14 }}>{isFile ? '📎' : categoryIcon(doc.Category__c)}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{doc.Category__c}</span>
                      {isFile && <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text3)' }}>{doc.File_Name__c}</span>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{doc.Title__c}</div>
                    {preview && !isFile && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{preview}</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)' }}>{doc.Author__c} · {formatDate(doc.CreatedDate)}</span>
                      {doc.Tags__c && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {doc.Tags__c.split(',').slice(0, 3).map(tag => (
                            <span key={tag.trim()} style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--accent)', background: 'rgba(124,109,250,0.1)', borderRadius: 4, padding: '1px 5px' }}>{tag.trim()}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : pinned.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📄</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text3)' }}>No docs yet. Create your first one.</div>
            </div>
          )}
        </>
      )}

      {/* Modals + Detail Panel */}
      {selectedDoc && <DocDetail doc={selectedDoc} onClose={() => setSelectedDoc(null)} onUpdated={handleDocUpdated} onDeleted={handleDocDeleted} />}
      {showNewDoc && <NewDocModal onClose={() => setShowNewDoc(false)} onCreated={() => { setShowNewDoc(false); load(); }} />}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={() => { setShowUpload(false); load(); }} />}
    </div>
  );
}
